import {useEffect, type ComponentProps} from 'react'
import {tv} from 'tailwind-variants'

import {Separator} from 'src/components/separator'
import useWindowSize from 'src/components/slider/hooks/useWindowSize'
import {cn} from 'src/layouts/main-layout/app-sidebar/utils/utils'
import {CompaniesSelector} from 'src/layouts/main-layout/companies-selector/companies-selector'
import config from 'src/services/config'
import {AppSidebarCollapseButton} from './app-sidebar-collapse-button'
import {useAppSidebar} from './app-sidebar-context'
import {AppSidebarNav} from './app-sidebar-nav'

export interface AppSidebarProps extends ComponentProps<'aside'> {}

const sidebarVariants = tv({
  base: [
    'flex h-full flex-col',
    'border-r border-border',
    'bg-sidebar text-foreground',
    'px-3 py-6',
    'overflow-auto',
    'min-w-0',
    'will-change-[width]',
    'transition-[width] duration-300 ease-in-out motion-reduce:transition-none',
  ],
})

export function AppSidebar({className, ...props}: AppSidebarProps) {
  const {collapsed, setCollapsed} = useAppSidebar()
  const {width} = useWindowSize()

  const mobileWidth = width <= 767

  useEffect(() => {
    if (mobileWidth) setCollapsed(true)
  }, [mobileWidth, setCollapsed])

  const sidebarWidth = mobileWidth ? 260 : collapsed ? 65 : 260

  return (
    <aside
      data-slot='sidebar'
      style={{width: sidebarWidth}}
      data-collapsed={collapsed ? '' : undefined}
      className={cn(
        sidebarVariants(),
        mobileWidth && [
          'fixed top-[55px] left-0 z-50 h-[calc(100dvh-55px)] border-r border-border shadow-xl',
          'transition-transform transition-opacity duration-300 ease-in-out motion-reduce:transition-none',
          collapsed ? '-translate-x-full opacity-0 pointer-events-none' : 'translate-x-0 opacity-100 pointer-events-auto',
        ],
        className,
      )}
      {...props}>
      <div
        data-slot='app-side-bar-container-collapsed'
        className={cn('flex w-full max-w-full items-center justify-center gap-3', collapsed && 'flex-col-reverse px-0')}>
        <CompaniesSelector />
        {!mobileWidth && <AppSidebarCollapseButton />}
      </div>

      <Separator className='bg-sidebar-border my-6' />

      <AppSidebarNav />

      <div className='flex-1' />
      {!collapsed && <div className='text-[#F2F2F0E5] text-[10px] flex justify-center items-center font-bold'>Versão: {config.version}</div>}
    </aside>
  )
}
